import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const UTC_TIMEZONE = 'UTC';

export const isValidTimezone = value => {
  if (typeof value !== 'string' || value.trim() === '') return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

export const resolveTimezone = ({ selectedTimezone, deviceTimezone, startTimezone } = {}) =>
  [selectedTimezone, deviceTimezone, startTimezone, UTC_TIMEZONE]
    .find(isValidTimezone) || UTC_TIMEZONE;

/** Formats a backend UTC value in the active counselor/device timezone. */
export const formatUtcDateTime = (value, timezoneName, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (value === null || value === undefined || value === '') return '-';
  const instant = dayjs.utc(value);
  return instant.isValid() ? instant.tz(timezoneName || UTC_TIMEZONE).format(format) : String(value);
};

/** Converts a date/time value from the active timezone into a UTC ISO-8601 string. */
export const toUtcIso = (value, timezoneName) => {
  if (value === null || value === undefined || value === '') return value;
  if (typeof value === 'string' && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) {
    const instant = dayjs(value);
    return instant.isValid() ? instant.utc().toISOString() : value;
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return dayjs.tz(value, timezoneName || UTC_TIMEZONE).utc().toISOString();
};

/** Converts a local date/time into the legacy UTC offset format required by service information. */
export const toUtcOffsetTimestamp = (value, timezoneName) => {
  const isoValue = toUtcIso(value, timezoneName);
  if (isoValue === null || isoValue === undefined || isoValue === '') return isoValue;
  const instant = dayjs.utc(isoValue);
  return instant.isValid() ? instant.format('YYYY-MM-DDTHH:mm:ssZZ') : value;
};

const UTC_DATE_TIME_FIELD = /(?:^|_)(?:ctime|timestamp|datetime|date|time)$|(?:Date|Time|DateTime)$/;
const TIME_ONLY_VALUE = /^\d{2}:\d{2}(?::\d{2})?$/;

/** Converts explicitly named date/time fields in an outbound API body to UTC. */
export const normalizeApiDateTimes = (
  value,
  timezoneName,
  preserveUtcDateTimeFields = []
) => {
  const preservedFields = new Set(preserveUtcDateTimeFields);
  if (Array.isArray(value)) {
    return value.map(item =>
      normalizeApiDateTimes(item, timezoneName, preserveUtcDateTimeFields)
    );
  }
  if (value === null || typeof value !== 'object' || value instanceof Date) {
    return value;
  }

  return Object.fromEntries(Object.entries(value).map(([key, fieldValue]) => {
    if (
      typeof fieldValue === 'string' &&
      UTC_DATE_TIME_FIELD.test(key) &&
      !TIME_ONLY_VALUE.test(fieldValue) &&
      !preservedFields.has(key)
    ) {
      return [key, toUtcIso(fieldValue, timezoneName)];
    }
    return [
      key,
      normalizeApiDateTimes(fieldValue, timezoneName, preserveUtcDateTimeFields),
    ];
  }));
};
